import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Button from '../../Common/Button';
import { CsvToIntArray } from '../../../helpers/strings';
import DatePicker from '../../Common/DatePicker/DatePicker';
import Toggle from '../../Common/Toggle/Toggle';
import { getDateValue } from '../../../helpers/reports';
import '../Reports.scss';

export class ProjectLocListFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            projectId: '', // single-select
            baselineIds: '',
            projectZeroDate: '', // single-select
            certifiedDateStart: '',
            certifiedDateEnd: '',
            brandIds: '',
            continentIds: '',
            regionIds: '',
            areaIds: '',
            locationTypeIds: '',
            countryIds: '',
            managementCompanyIds: '',
            openingDateStart: '',
            openingDateEnd: '',
            isLocationActive: true
        }

        this.state = {
            emptyForm: initialFormSelections,
            showMoreFields: false,

            projectId: initialFormSelections.projectId, // single-select
            baselineIds: initialFormSelections.baselineIds,
            projectZeroDate: initialFormSelections.projectZeroDate, // single-select
            certifiedDateStart: initialFormSelections.certifiedDateStart,
            certifiedDateEnd: initialFormSelections.certifiedDateEnd,
            brandIds: initialFormSelections.brandIds,
            continentIds: initialFormSelections.continentIds,
            regionIds: initialFormSelections.regionIds,
            areaIds: initialFormSelections.areaIds,
            locationTypeIds: initialFormSelections.locationTypeIds,
            countryIds: initialFormSelections.countryIds,
            managementCompanyIds: initialFormSelections.managementCompanyIds,
            openingDateStart: initialFormSelections.openingDateStart,
            openingDateEnd: initialFormSelections.openingDateEnd,
            isLocationActive: initialFormSelections.isLocationActive
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;

        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,

                projectId: appState.projectId ? appState.projectId : newState.projectId,
                baselineIds: appState.baselineIds ? appState.baselineIds : newState.baselineIds,
                projectZeroDate: appState.projectZeroDate ? appState.projectZeroDate : newState.projectZeroDate,
                certifiedDateStart: appState.certifiedDateStart ? appState.certifiedDateStart : newState.certifiedDateStart,
                certifiedDateEnd: appState.certifiedDateEnd ? appState.certifiedDateEnd : newState.certifiedDateEnd,
                brandIds: appState.brandIds ? appState.brandIds : newState.brandIds,
                continentIds: appState.continentIds ? appState.continentIds : newState.continentIds,
                regionIds: appState.regionIds ? appState.regionIds : newState.regionIds,
                areaIds: appState.areaIds ? appState.areaIds : newState.areaIds,
                locationTypeIds: appState.locationTypeIds ? appState.locationTypeIds : newState.locationTypeIds,
                countryIds: appState.countryIds ? appState.countryIds : newState.countryIds,
                managementCompanyIds: appState.managementCompanyIds ? appState.managementCompanyIds : newState.managementCompanyIds,
                openingDateStart: appState.openingDateStart ? appState.openingDateStart : newState.openingDateStart,
                openingDateEnd: appState.openingDateEnd ? appState.openingDateEnd : newState.openingDateEnd,
                isLocationActive: appState.isLocationActive ? appState.isLocationActive : newState.isLocationActive
            }
        }

        if (this.props.selectedProject) {
            const selectedProjectName = this.props.selectedProject;
            const selectedProject = this.props.filters.projects.find(project => project.name === selectedProjectName);
        
            if (selectedProject) {
                newState = {
                    ...newState,
                    projectId: selectedProject.id
                };
            }
        }

        // Possibly override app state values with default values or URL params
        let projectId = this.getDefaultProjectId(newState.projectId);

        newState = {
            ...newState,
            projectId: projectId
        }

        this.setState({
            ...this.state,
            ...newState
        });

        /**
         * If newState.projectId has a value, then we need to trigger a project
         * change to make sure we immediately load the ProjectZeroDate and 
         * Project Baseline options, because this does not get automatically 
         * triggered just by setting the value in state.
         */
        if (newState.projectId) {
            this.props.onProjectChange(newState.projectId);
        }
    }

    getDefaultProjectId = (savedProjectId) => {
        /**
         * Project IDs(s) can be sent in via the URL params.  If they are set there
         * then we override the default or saved values and select the values from
         * the URL params
         */
        let projectId = '';
        const urlProjectId = this.props.urlParams.projectId;
        if (urlProjectId && urlProjectId.trim().length > 0) {
            projectId = urlProjectId;
        } else if (savedProjectId) {
            projectId = savedProjectId;
        }

        return projectId;
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.projectId) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.projectId = (reportRequest.projectId) ? reportRequest.projectId : form.projectId;
        form.baselineIds = (reportRequest.baselineIds) ? reportRequest.baselineIds.join(',') : form.baselineIds;
        form.projectZeroDate = getDateValue(reportRequest.projectZeroDate);
        form.certifiedDateStart = getDateValue(reportRequest.certifiedDateStart);
        form.certifiedDateEnd = getDateValue(reportRequest.certifiedDateEnd);
        form.brandIds = (reportRequest.brandIds) ? reportRequest.brandIds.join(',') : form.brandIds;
        form.continentIds = (reportRequest.continentIds) ? reportRequest.continentIds.join(',') : form.continentIds;
        form.regionIds = (reportRequest.regionIds) ? reportRequest.regionIds.join(',') : form.regionIds;
        form.areaIds = (reportRequest.areaIds) ? reportRequest.areaIds.join(',') : form.areaIds;
        form.locationTypeIds = (reportRequest.locationTypeIds) ? reportRequest.locationTypeIds.join(',') : form.locationTypeIds;
        form.countryIds = (reportRequest.countryIds) ? reportRequest.countryIds.join(',') : form.countryIds;
        form.managementCompanyIds = (reportRequest.managementCompanyIds) ? reportRequest.managementCompanyIds.join(',') : form.managementCompanyIds;
        form.openingDateStart = getDateValue(reportRequest.openingDateStart);
        form.openingDateEnd = getDateValue(reportRequest.openingDateEnd);
        form.isLocationActive = (reportRequest.isLocationActive === true) ? true : false;

        this.setState({
            ...this.state,
            ...form
        });

        /** 
         * Make sure to trigger the parent's loading of the proper project dependent
         * filter values
         */
        if (reportRequest.projectId) {
            this.props.onProjectChange(reportRequest.projectId);
        }
    }

    /**
     * When an API request is going to be made, we also want to save the state
     * of our form entries into redux, so that saved state can be loaded when
     * this report is scheduled or simply reloaded later.  To accomplish that
     * from the centralized ReportDataHelper, we return both the apiRequest
     * object as well as the current state of our form.  This separation allows
     * for differences in the apiRequest format/naming conventions and those of
     * the form fields.
     */
    mapToApiRequest = () => {

        const request = {
            reportId: this.props.report.id,

            projectId: this.state.projectId,
            baselineIds: CsvToIntArray(this.state.baselineIds),
            projectZeroDate: this.state.projectZeroDate,
            certifiedDateStart: this.state.certifiedDateStart,
            certifiedDateEnd: this.state.certifiedDateEnd,
            brandIds: CsvToIntArray(this.state.brandIds),
            continentIds: CsvToIntArray(this.state.continentIds),
            regionIds: CsvToIntArray(this.state.regionIds),
            areaIds: CsvToIntArray(this.state.areaIds),
            locationTypeIds: CsvToIntArray(this.state.locationTypeIds),
            countryIds: CsvToIntArray(this.state.countryIds),
            managementCompanyIds: CsvToIntArray(this.state.managementCompanyIds),
            openingDateStart: this.state.openingDateStart,
            openingDateEnd: this.state.openingDateEnd,
            isLocationActive: this.state.isLocationActive
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({
            ...this.state,
            [name]: value
        });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    onProjectChange = (value, name) => {
        // Save the changed value in our local state, as usual
        this.onFieldChange(name, value);

        // Tell our parent the project value has changed so they can reload the
        // options for the Project Zero Date and Baseline fields
        if (value) {
            this.props.onProjectChange(value);
        }
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";

        /**
         * Project Dependent Filters don't exist until a project has been selected,
         * so we must ensure we send MultiSelectBox an empty array instead of undefined
         * or it throws errors
         */
        const projectZeroDateOptions = this.props.filters.projectZeroDates ? this.props.filters.projectZeroDates : [];
        const projectBaselineOptions = this.props.filters.projectBaselines ? this.props.filters.projectBaselines : [];

        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectId"
                            label="Project" idField="id" valueField="name"
                            options={this.props.filters.projects}
                            selectedValues={this.state.projectId.toString()}
                            onChange={this.onProjectChange}
                            isRequired={true}
                            errorMessage="Project is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="baselineIds"
                            label="Baseline" idField="id" valueField="name"
                            options={projectBaselineOptions}
                            selectedValues={this.state.baselineIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectZeroDate"
                            label="Project Zero Date / Anticipated Completion" idField="id" valueField="name"
                            options={projectZeroDateOptions}
                            selectedValues={this.state.projectZeroDate.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="certifiedDateStart"
                            value={this.state.certifiedDateStart}
                            label="Certified Date Start"
                            onChange={this.onDatePickerChange} />
                    </div>
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="certifiedDateEnd"
                            value={this.state.certifiedDateEnd}
                            label="End"
                            onChange={this.onDatePickerChange} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="brandIds"
                            label="Brand" idField="id" valueField="name"
                            options={this.props.filters.brands}
                            selectedValues={this.state.brandIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="continentIds"
                            label="Continent" idField="id" valueField="name"
                            options={this.props.filters.continents}
                            selectedValues={this.state.continentIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                {
                    this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="regionIds"
                                    label="Region" idField="id" valueField="name"
                                    options={this.props.filters.regions}
                                    selectedValues={this.state.regionIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="areaIds"
                                    label="Area" idField="id" valueField="name"
                                    options={this.props.filters.areas}
                                    selectedValues={this.state.areaIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="locationTypeIds"
                                    label="Location Type" idField="id" valueField="name"
                                    options={this.props.filters.locationTypes}
                                    selectedValues={this.state.locationTypeIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="countryIds"
                                    label="Country" idField="id" valueField="name"
                                    options={this.props.filters.countries}
                                    selectedValues={this.state.countryIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="managementCompanyIds"
                                    label="Management Company" idField="id" valueField="name"
                                    options={this.props.filters.managementCompanies}
                                    selectedValues={this.state.managementCompanyIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-2">
                                <DatePicker showIcon name="openingDateStart"
                                    value={this.state.openingDateStart}
                                    label="Opening Date Start"
                                    onChange={this.onDatePickerChange} />
                            </div>
                            <div className="col-sm-12 col-md-2">
                                <DatePicker showIcon name="openingDateEnd"
                                    value={this.state.openingDateEnd}
                                    label="End"
                                    onChange={this.onDatePickerChange} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <Toggle name="isLocationActive"
                                    text="Active Locations"
                                    checked={this.state.isLocationActive}
                                    onChange={this.onFieldChange} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

ProjectLocListFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    selectedProject: PropTypes.string,

    registerReportFilterFunctions: PropTypes.func.isRequired,
    onProjectChange: PropTypes.func.isRequired
};

export default ProjectLocListFilters;
